import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/analytics"

// Manages the category filter tabs + "Mostrar mais" pagination on the
// car/feature pages. Filters items by cluster/tag and limits the visible
// count when no specific cluster is selected.
export default class extends Controller {
  static targets = ["tab", "item", "showMoreBtn"]

  static values = {
    active: { type: String, default: "all" },
    initialLimit: { type: Number, default: 10 },
    pageSize: { type: Number, default: 10 }
  }

  connect() {
    this.shownLimit = this.initialLimitValue
    this.firedPlaylistStarts = new Set()
    // Re-snap to full rows when the column count changes on resize.
    this.onResize = () => { clearTimeout(this._resizeTimer); this._resizeTimer = setTimeout(() => this.filter(), 150) }
    window.addEventListener("resize", this.onResize)
    this.filter()
  }

  disconnect() {
    if (this.onResize) window.removeEventListener("resize", this.onResize)
  }

  select(event) {
    event.preventDefault()
    const newCategory = event.currentTarget.dataset.category || "all"
    const playlistId = event.currentTarget.dataset.playlistId || null
    this.activeValue = newCategory
    // Reset pagination when switching tabs
    this.shownLimit = this.initialLimitValue

    this.tabTargets.forEach(tab => {
      if ((tab.dataset.category || "all") === this.activeValue) {
        tab.classList.add("active")
      } else {
        tab.classList.remove("active")
      }
    })

    this.filter()
    this.trackPlaylistStart(newCategory, playlistId)
  }

  trackPlaylistStart(category, playlistId) {
    // Skip the synthetic "recomendados"/"all"/"mais-vistos"/"mais-recentes"
    // tabs — those aren't real playlists. Only fire for actual playlist
    // UUIDs and only once per tab per page load.
    if (!category || category === "recomendados" || category === "all" || category === "mais-vistos" || category === "mais-recentes") return
    if (!playlistId) return
    if (this.firedPlaylistStarts.has(playlistId)) return
    this.firedPlaylistStarts.add(playlistId)
    track("playlist_start", {
      playlist_id: playlistId,
      media_item_ids: this.mediaItemIdsFor(category)
    })
  }

  mediaItemIdsFor(category) {
    const ids = []
    this.itemTargets.forEach(item => {
      const cats = (item.dataset.category || "").split(/\s+/).filter(Boolean)
      if (!cats.includes(category)) return
      if (item.dataset.mediaItemId) ids.push(item.dataset.mediaItemId)
    })
    return [...new Set(ids)]
  }

  showMore(event) {
    event.preventDefault()
    this.shownLimit += this.pageSizeValue
    this.filter()
  }

  filter() {
    const active = this.activeValue
    const isPlaylist = active !== "all"

    let matching = this.itemTargets.filter(item => {
      const cats = (item.dataset.category || "").split(/\s+/).filter(Boolean)
      return active === "all" || cats.includes(active)
    })

    // Honor the playlist's configured order (feature_playlist_membership
    // position) when a playlist tab is selected — re-order the DOM so the grid
    // matches the playlist, then carry the playlist into each feature link so
    // the detail page's Próximo/Anterior walks the same order.
    if (isPlaylist && matching.length) {
      matching = matching.slice().sort((a, b) => this.positionFor(a, active) - this.positionFor(b, active))
      const container = matching[0].parentNode
      matching.forEach(item => container.appendChild(item))
    }

    this.itemTargets.forEach(item => this.setPlaylistParam(item, isPlaylist ? active : null))

    // Show whole rows only — snap the budget down to a multiple of the current
    // column count so desktop never shows a partial trailing line. When the
    // budget already covers everything, show all (the final line may be partial,
    // but there's nothing more to reveal). "Ver mais" shows only when items
    // remain — i.e. there's at least one more full line to reveal.
    const perRow = this.itemsPerRow()
    const limit = this.shownLimit >= matching.length
      ? matching.length
      : Math.max(perRow, Math.floor(this.shownLimit / perRow) * perRow)

    let shownCount = 0
    this.itemTargets.forEach(item => { item.style.display = "none" })
    matching.forEach(item => {
      if (shownCount < limit) { item.style.display = ""; shownCount++ }
    })

    if (this.hasShowMoreBtnTarget) {
      this.showMoreBtnTarget.style.display = matching.length > shownCount ? "" : "none"
    }
  }

  // Number of grid columns currently rendered (1 when the list is a plain
  // block, e.g. on mobile). Read from the resolved grid template so it tracks
  // the responsive CSS breakpoints.
  itemsPerRow() {
    const container = this.hasItemTarget ? this.itemTargets[0].parentNode : null
    if (!container) return 1
    const cols = getComputedStyle(container).gridTemplateColumns
    if (!cols || cols === "none") return 1
    const count = cols.split(" ").filter(Boolean).length
    return count > 0 ? count : 1
  }

  positionFor(item, playlistSlug) {
    try {
      const map = JSON.parse(item.dataset.positions || "{}")
      return playlistSlug in map ? Number(map[playlistSlug]) : Number.MAX_SAFE_INTEGER
    } catch (_) {
      return Number.MAX_SAFE_INTEGER
    }
  }

  // Add/strip ?playlist= on a feature link so the detail page knows which
  // playlist context its Próximo/Anterior should navigate within.
  setPlaylistParam(item, playlistSlug) {
    const anchor = item.tagName === "A" ? item : item.querySelector("a")
    if (!anchor) return
    try {
      const url = new URL(anchor.getAttribute("href"), window.location.origin)
      if (playlistSlug) url.searchParams.set("playlist", playlistSlug)
      else url.searchParams.delete("playlist")
      anchor.setAttribute("href", url.pathname + url.search)
    } catch (_) { /* leave href untouched on parse error */ }
  }
}
